import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatEUR } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Minus, Plus, Check, ArrowLeft } from "lucide-react";
import SEO, { buildBreadcrumb, toAbsolute, CANONICAL_ORIGIN } from "../components/SEO";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { addItem, setIsOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data));
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    api
      .get("/products")
      .then((r) =>
        setRelated(
          r.data
            .filter((p) => p.id !== product.id && p.category === product.category)
            .slice(0, 3)
        )
      )
      .catch(() => setRelated([]));
  }, [product]);

  const handleAdd = () => {
    if (!product) return;
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
    setTimeout(() => setIsOpen(true), 300);
  };

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="animate-pulse grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-[color:var(--brand-bg-alt)] rounded-md" />
          <div className="space-y-4">
            <div className="h-8 bg-[color:var(--brand-bg-alt)] rounded w-2/3" />
            <div className="h-4 bg-[color:var(--brand-bg-alt)] rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
      <SEO
        title={`${product.name} · Mimes Gourmet`}
        description={
          product.description && product.description.length > 20
            ? product.description.slice(0, 158)
            : `${product.name} artesana elaborada por Mimes Gourmet en Graus (Pirineo aragonés). Tarro de ${product.weight_g}g.`
        }
        path={`/producto/${product.slug}`}
        image={product.image}
        type="product"
        noindex={!!product.is_hidden}
        jsonLd={
          product.is_hidden
            ? []
            : [
                {
                  "@context": "https://schema.org",
                  "@type": "Product",
                  name: product.name,
                  description: product.long_description || product.description,
                  image: [toAbsolute(product.image)],
                  sku: product.id,
                  category: product.category === "salado" ? "Salado" : "Dulce",
                  brand: { "@type": "Brand", name: "Mimes Gourmet" },
                  manufacturer: {
                    "@type": "Organization",
                    name: "Mimes Gourmet",
                    address: {
                      "@type": "PostalAddress",
                      streetAddress: "Calle Fray Diego Cera, 21",
                      postalCode: "22430",
                      addressLocality: "Graus",
                      addressRegion: "Huesca",
                      addressCountry: "ES",
                    },
                  },
                  weight: {
                    "@type": "QuantitativeValue",
                    value: product.weight_g,
                    unitCode: "GRM",
                  },
                  offers: {
                    "@type": "Offer",
                    url: `${CANONICAL_ORIGIN}/producto/${product.slug}`,
                    priceCurrency: "EUR",
                    price: product.price_eur.toFixed(2),
                    priceValidUntil: "2027-12-31",
                    availability:
                      product.stock > 0
                        ? "https://schema.org/InStock"
                        : "https://schema.org/OutOfStock",
                    itemCondition: "https://schema.org/NewCondition",
                    seller: { "@type": "Organization", name: "Mimes Gourmet" },
                    priceSpecification: {
                      "@type": "UnitPriceSpecification",
                      price: product.price_eur.toFixed(2),
                      priceCurrency: "EUR",
                      valueAddedTaxIncluded: true,
                    },
                    // Shipping declared here reflects the base cost that applies to
                    // this product at any time (España peninsular, 4,95 €, 2-4 días).
                    // The "free shipping ≥ 100 €" is a cart-level threshold, not a
                    // property of the product itself, so Schema.org has no clean way
                    // to represent it inside OfferShippingDetails. It is communicated
                    // to the customer during checkout and is best declared via Google
                    // Merchant Center Feed, not here — declaring `minValue` inside
                    // `shippingRate` would be misinterpreted by Google.
                    shippingDetails: {
                      "@type": "OfferShippingDetails",
                      shippingRate: {
                        "@type": "MonetaryAmount",
                        value: "4.95",
                        currency: "EUR",
                      },
                      shippingDestination: {
                        "@type": "DefinedRegion",
                        addressCountry: "ES",
                      },
                      deliveryTime: {
                        "@type": "ShippingDeliveryTime",
                        handlingTime: {
                          "@type": "QuantitativeValue",
                          minValue: 0,
                          maxValue: 1,
                          unitCode: "DAY",
                        },
                        transitTime: {
                          "@type": "QuantitativeValue",
                          minValue: 2,
                          maxValue: 4,
                          unitCode: "DAY",
                        },
                      },
                    },
                  },
                },
                buildBreadcrumb([
                  { name: "Inicio", path: "/" },
                  { name: "Tienda", path: "/tienda" },
                  { name: product.name, path: `/producto/${product.slug}` },
                ]),
              ]
        }
      />
      <Link
        to="/tienda"
        data-testid="back-to-shop"
        className="inline-flex items-center gap-1.5 text-sm text-[color:var(--brand-neutral)] hover:text-[color:var(--brand-primary)] mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Volver a la tienda
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        <div className="lg:sticky lg:top-24 self-start">
          <div className="relative aspect-square overflow-hidden rounded-md bg-[color:var(--brand-bg-alt)]">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-hero-image"
            />
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {product.is_bestseller && (
                <span className="text-[10px] tracking-widest uppercase font-semibold bg-[color:var(--brand-primary)] text-[color:var(--brand-bg)] px-3 py-1.5 rounded-sm">
                  Best Seller
                </span>
              )}
            </div>
          </div>
        </div>

        <div>
          <p className="eyebrow mb-3">
            {product.category === "salado" ? "Salado" : "Dulce"} · {product.batch}
          </p>
          <h1 data-testid="product-name" className="font-serif text-4xl sm:text-5xl text-[color:var(--brand-primary)] leading-tight font-light">
            {product.name}
          </h1>
          <p data-testid="product-price" className="mt-4 font-serif text-3xl text-[color:var(--brand-primary)]">
            {formatEUR(product.price_eur)}
            <span className="text-sm text-[color:var(--brand-neutral)]/70 ml-2">· tarro de {product.weight_g}g</span>
          </p>

          <p className="mt-6 text-base leading-relaxed text-[color:var(--brand-neutral)]">{product.long_description}</p>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-[color:var(--brand-border)] rounded-md">
              <button
                data-testid="qty-dec"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="p-3 hover:bg-[color:var(--brand-bg-alt)]"
                aria-label="Reducir cantidad"
              >
                <Minus className="w-4 h-4" strokeWidth={1.6} />
              </button>
              <span data-testid="qty-value" className="px-4 text-base min-w-10 text-center font-medium">
                {qty}
              </span>
              <button
                data-testid="qty-inc"
                onClick={() => setQty((q) => Math.min(50, q + 1))}
                className="p-3 hover:bg-[color:var(--brand-bg-alt)]"
                aria-label="Aumentar cantidad"
              >
                <Plus className="w-4 h-4" strokeWidth={1.6} />
              </button>
            </div>
            <Button
              data-testid="detail-add-to-cart"
              onClick={handleAdd}
              className={`flex-1 rounded-md py-6 font-medium tracking-wide transition-all ${
                added
                  ? "bg-[color:var(--brand-sage)] hover:bg-[color:var(--brand-sage)] text-white"
                  : "bg-[color:var(--brand-primary)] hover:bg-black text-white"
              }`}
            >
              {added ? (
                <span className="flex items-center gap-2 animate-soft-ping">
                  <Check className="w-4 h-4" /> Añadido
                </span>
              ) : (
                `Añadir a la cesta · ${formatEUR(product.price_eur * qty)}`
              )}
            </Button>
          </div>

          <div className="mt-6 flex items-center gap-2 text-xs text-[color:var(--brand-neutral)]/80">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--brand-sage)]" />
            <span>{product.stock > 10 ? "En stock · envío 24-48h" : `Solo quedan ${product.stock} tarros`}</span>
          </div>

          <Accordion type="single" collapsible defaultValue="ing" className="mt-10">
            <AccordionItem value="ing" className="border-t border-[color:var(--brand-border)]">
              <AccordionTrigger data-testid="accordion-ingredients" className="font-serif text-lg text-[color:var(--brand-primary)] hover:no-underline">
                Ingredientes
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[color:var(--brand-neutral)] leading-relaxed">
                {product.ingredients.join(", ")}.
                {product.allergens.length > 0 && (
                  <p className="mt-3 text-xs text-[color:var(--brand-terracotta)]">
                    <strong>Alérgenos:</strong> {product.allergens.join(", ")}
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="pair" className="border-t border-[color:var(--brand-border)]">
              <AccordionTrigger data-testid="accordion-pairing" className="font-serif text-lg text-[color:var(--brand-primary)] hover:no-underline">
                Sugerencias de maridaje
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[color:var(--brand-neutral)] leading-relaxed">
                {product.pairing}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-t border-b border-[color:var(--brand-border)]">
              <AccordionTrigger className="font-serif text-lg text-[color:var(--brand-primary)] hover:no-underline">
                Envío y conservación
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[color:var(--brand-neutral)] leading-relaxed space-y-2">
                <p>Envíos en 24-48h en España peninsular. Gratis desde 100€.</p>
                <p>Conservar en lugar fresco. Una vez abierto, refrigerar y consumir en 3 semanas.</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-20 pt-12 border-t border-[color:var(--brand-border)]" data-testid="related-products">
          <p className="eyebrow mb-3">Quizá también te guste</p>
          <h2 className="font-serif text-2xl sm:text-3xl text-[color:var(--brand-primary)] font-light mb-8">
            Otros sabores {product.category === "salado" ? "salados" : "dulces"}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/producto/${r.slug}`}
                data-testid={`related-${r.slug}`}
                className="group block"
              >
                <div className="aspect-square overflow-hidden rounded-md bg-[color:var(--brand-bg-alt)]">
                  <img
                    src={r.image}
                    alt={r.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <p className="mt-4 font-serif text-lg text-[color:var(--brand-primary)] group-hover:text-[color:var(--brand-terracotta)]">
                  {r.name}
                </p>
                <p className="text-sm text-[color:var(--brand-neutral)]/70 mt-1">
                  {formatEUR(r.price_eur)} · tarro de {r.weight_g}g
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
